import { baseApi } from "@/shared/api";
import type {
  GetMeResponse,
  LoginDto,
  LoginResponse,
  LogoutResponse,
  ChangePasswordDto,
  ChangePasswordResponse,
  ForgotPasswordDto,
  ForgotPasswordResponse,
  ResetPasswordDto,
  ResetPasswordResponse,
  UserRoleAssignment,
  AssignRoleRequest,
  SyncUserRolesRequest,
  SyncUserRolesResponse,
  GetUserRolesResponse,
  PendingCountResponse,
} from "./types";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginDto>({
      query: (body) => ({
        url: "/api/auth/login/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: ["Auth"],
    }),

    getMe: builder.query<GetMeResponse, void>({
      query: () => ({
        url: "/api/auth/me/",
        credentials: "include",
      }),
      providesTags: ["Auth"],
    }),

    changePassword: builder.mutation<ChangePasswordResponse, { data: ChangePasswordDto; tempToken?: string }>({
      query: ({ data, tempToken }) => ({
        url: "/api/auth/change-password/",
        method: "POST",
        body: data,
        credentials: "include",
        headers: tempToken ? { Authorization: `Bearer ${tempToken}` } : {},
      }),
      invalidatesTags: ["Auth"],
    }),

    logout: builder.mutation<LogoutResponse, void>({
      query: () => ({
        url: "/api/auth/logout/",
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: ["Auth"],
    }),

    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordDto>({
      query: (body) => ({
        url: "/api/auth/forgot-password/",
        method: "POST",
        body,
        credentials: "include",
      }),
    }),

    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordDto>({
      query: (body) => ({
        url: "/api/auth/reset-password/",
        method: "POST",
        body,
        credentials: "include",
      }),
    }),

    assignUserRole: builder.mutation<UserRoleAssignment, { userId: number; roleId: number }>({
      query: ({ userId, roleId }) => ({
        url: `/api/auth/users/${userId}/roles/`,
        method: "POST",
        body: { role_id: roleId } as AssignRoleRequest,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "NGOWorkers", id: "LIST" }],
    }),

    removeUserRole: builder.mutation<void, { userId: number; roleId: number }>({
      query: ({ userId, roleId }) => ({
        url: `/api/auth/users/${userId}/roles/${roleId}/`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: [{ type: "NGOWorkers", id: "LIST" }],
    }),

    getUserRoles: builder.query<GetUserRolesResponse, number>({
      query: (userId) => ({
        url: `/api/auth/users/${userId}/roles/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "NGOWorkers" as const, id: "LIST" }],
    }),

    syncUserRoles: builder.mutation<SyncUserRolesResponse, { userId: number; roles: string[] }>({
      query: ({ userId, roles }) => ({
        url: `/api/auth/users/${userId}/roles/`,
        method: "PUT",
        body: { roles } as SyncUserRolesRequest,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "NGOWorkers", id: "LIST" }],
    }),

    getPendingCount: builder.query<PendingCountResponse, void>({
      query: () => ({
        url: "/api/auth/pending-count/",
        method: "GET",
        credentials: "include",
      }),
      providesTags: ["PendingActions"],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetMeQuery,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useAssignUserRoleMutation,
  useRemoveUserRoleMutation,
  useGetUserRolesQuery,
  useSyncUserRolesMutation,
  useGetPendingCountQuery,
} = authApi;
